import { Text, ReactTable, Box } from '@wegroup/design-system';
import { capitalize } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell } from 'react-table';
import { handleResponseLanguage } from '../../../../../../../../i18n';
import useTextualRepresentation from '../../../../../hooks/useTextualRepresentation';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import TableFiller from '../../common/TableFiller';
import useTableColumns from '../hooks/useTableColumns';
import useTableItemObjects, { TableRow } from '../hooks/useTableItemObjects';
import BuildingActionsMenu from './TableItemActionsMenu';

interface Props {
  question: FlowsQuestion;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
}

const ItemsTable: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  onEdit,
  onRemove,
  isDisabled,
  isInvalid,
}) => {
  const { t } = useTranslation();
  const items = useTableItemObjects(question);
  const tableColumns = useTableColumns(question);
  const getTextAnswer = useTextualRepresentation();

  const entity_name = handleResponseLanguage(question.rules.table?.entity_name);
  const entity_name_plural = handleResponseLanguage(
    question.rules.table?.entity_name_plural,
  ).toLowerCase();

  const renderBuildingNumberCell = (cell: Cell) => {
    const index = cell?.row?.index;

    return (
      <Text color="text.gray">
        {entity_name} {index + 1}
      </Text>
    );
  };

  const handleDynamicColumn = (cell: Cell, questionId: string) => {
    const { index } = cell?.row?.original as TableRow;

    const answer = getTextAnswer(questionId, index);

    return <Text color="text.gray">{answer ? answer : '-'}</Text>;
  };

  const renderActionMenuCell = (cell: Cell) => {
    const { index } = cell?.row?.original as TableRow;
    return (
      <BuildingActionsMenu
        isDisabled={isDisabled}
        question={question}
        onEdit={() => onEdit(index)}
        onRemove={() => onRemove(index)}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: '',
        accessor: 'index',
        Cell: renderBuildingNumberCell,
        disableSortBy: true,
        isSticky: true,
      },
      ...tableColumns.map(({ questionId, label }) => ({
        Header: <Text dangerouslySetInnerHTML={{ __html: label }} />,
        accessor: questionId,
        Cell: (cell: Cell) => handleDynamicColumn(cell, questionId),
      })),
      {
        Header: (
          <Text w="100%" textAlign="right">
            {t('ACTIONS')}
          </Text>
        ),
        id: 'actions',
        Cell: renderActionMenuCell,
        disableSortBy: true,
        isSticky: true,
      },
    ],
    [items],
  );

  return (
    <Box>
      <ReactTable
        data={items}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columns={columns as any}
        borderColor={isInvalid ? 'danger.main' : 'gray.50'}
      />
      {items.length === 0 && (
        <TableFiller
          borderColor={isInvalid ? 'danger.main' : 'gray.strokeAndBg'}
        >
          <Text>
            {capitalize(
              t(`FLOWS_INPUT_TABLE_EMPTY-TABLE`, {
                entities: entity_name_plural,
              }),
            )}
          </Text>
        </TableFiller>
      )}
    </Box>
  );
};

export default ItemsTable;
